/**
 * Add Residency / Visa / Emirates ID (Feb 2026 / iter-181).
 * Requires selecting an existing company file. Supports AI OCR via
 * /api/agent/residencies/extract-id (user's own GEMINI_API_KEY).
 * On save → navigate back to the 5-button hub.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Upload, Sparkles, Loader2, Save, IdCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { AgentPageShell } from './agent/AgentPageShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyRow = {
  company_id: '',
  doc_type: 'residency',
  full_name_ar: '',
  full_name_en: '',
  nationality: '',
  gender: '',
  date_of_birth: '',
  emirates_id_number: '',
  passport_number: '',
  visa_number: '',
  file_number: '',
  uid_number: '',
  visa_type: '',
  sponsor: '',
  profession: '',
  issue_date: '',
  expiry_date: '',
  place_of_issue: '',
  notes: '',
};

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const [prefix, base64] = String(reader.result).split(',');
      const m = /^data:([^;]+);base64$/.exec(prefix);
      resolve({ base64, mime: m ? m[1] : file.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const AgentResidencyCreatePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [params] = useSearchParams();
  const preselected = params.get('company_id') || '';

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [form, setForm] = useState({ ...emptyRow, company_id: preselected });
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'إضافة إقامة / تأشيرة / هوية', label_en: 'Add Residency / Visa / ID', path: null },
  ];

  // Load companies dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/agent/companies`, {
          headers: getAuthHeaders(),
        });
        setCompanies(res.data?.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message);
      } finally {
        setCompaniesLoading(false);
      }
    })();
  }, []);

  const handleFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error(language === 'ar' ? 'الحجم الأقصى 10 ميغا' : 'Max 10 MB');
      return;
    }
    const { base64, mime } = await readFileAsBase64(f);
    setFile({ base64, mime, name: f.name });
  };

  const runAIExtract = async () => {
    if (!file) {
      toast.error(language === 'ar' ? 'ارفع الملف أولاً' : 'Upload the document first');
      return;
    }
    setExtracting(true);
    try {
      const res = await axios.post(
        `${API}/agent/residencies/extract-id`,
        { file_base64: file.base64, mime: file.mime },
        { headers: getAuthHeaders(), timeout: 90_000 }
      );
      const data = res.data?.data || {};
      setForm((f) => ({
        ...f,
        doc_type: data.doc_type && ['emirates_id', 'residency', 'visa'].includes(data.doc_type) ? data.doc_type : f.doc_type,
        full_name_ar: data.full_name_ar || f.full_name_ar,
        full_name_en: data.full_name_en || f.full_name_en,
        nationality: data.nationality || f.nationality,
        gender: data.gender || f.gender,
        date_of_birth: data.date_of_birth || f.date_of_birth,
        emirates_id_number: data.emirates_id_number || f.emirates_id_number,
        passport_number: data.passport_number || f.passport_number,
        visa_number: data.visa_number || f.visa_number,
        file_number: data.file_number || f.file_number,
        uid_number: data.uid_number || f.uid_number,
        visa_type: data.visa_type || f.visa_type,
        sponsor: data.sponsor || f.sponsor,
        profession: data.profession || f.profession,
        issue_date: data.issue_date || f.issue_date,
        expiry_date: data.expiry_date || f.expiry_date,
        place_of_issue: data.place_of_issue || f.place_of_issue,
      }));
      toast.success(language === 'ar' ? 'تمت القراءة بنجاح' : 'Read successfully');
    } catch (err) {
      toast.error((language === 'ar' ? 'تعذّرت القراءة: ' : 'AI extract failed: ') + (err.response?.data?.detail || err.message));
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!form.company_id) {
      toast.error(language === 'ar' ? 'اختر ملف شركة أولاً' : 'Select a company first');
      return;
    }
    if (!form.full_name_ar && !form.full_name_en) {
      toast.error(language === 'ar' ? 'الاسم إلزامي (عربي أو إنجليزي)' : 'Full name is required (AR or EN)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        document_file: file?.base64 || null,
        document_file_mime: file?.mime || '',
        document_file_name: file?.name || '',
      };
      await axios.post(`${API}/agent/residencies`, payload, {
        headers: getAuthHeaders(),
      });
      toast.success(language === 'ar' ? 'تم الحفظ' : 'Saved');
      // Feb 2026 — iter-181: navigate back to the 5-button hub.
      navigate('/manage/cloudgm-agent');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AgentPageShell
      breadcrumb={breadcrumb}
      maxWidth="max-w-5xl"
      onBack={() => navigate('/manage/cloudgm-agent')}
    >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2 text-white" data-testid="agent-residency-create-title">
          <IdCard className="h-6 w-6 text-cyan-400" />
          {language === 'ar' ? 'إضافة إقامة / تأشيرة / هوية' : 'Add Residency / Visa / ID'}
        </h1>
      </div>

        {/* Company selector — iter-244: hidden when opened from inside a
           specific company file (URL query `?company_id=...`). Instead show
           a read-only "company header" pill so the operator always knows
           which file they're inside without having to pick again. */}
        {preselected ? (
          <motion.section
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 mb-6 flex items-center gap-3"
            data-testid="agent-residency-preselected-company"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300">🏢</div>
            <div className="flex-1">
              <div className="text-[10px] text-emerald-300 uppercase tracking-wide">{language === 'ar' ? 'ملف الشركة' : 'Company file'}</div>
              <div className="text-white font-semibold">
                {(() => {
                  const c = companies.find((x) => x.id === form.company_id);
                  if (!c) return language === 'ar' ? 'جاري التحميل…' : 'Loading…';
                  return (language === 'ar' ? (c.company_name_ar || c.company_name_en) : (c.company_name_en || c.company_name_ar))
                    + (c.license_number ? ` — ${c.license_number}` : '');
                })()}
              </div>
            </div>
          </motion.section>
        ) : (
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6"
        >
          <Label>
            {language === 'ar' ? 'اختر ملف الشركة (إلزامي)' : 'Select company file (required)'}
          </Label>
          {companiesLoading ? (
            <p className="text-sm text-[#808080] mt-2">{language === 'ar' ? 'جاري التحميل…' : 'Loading…'}</p>
          ) : companies.length === 0 ? (
            <div className="mt-2">
              <p className="text-sm text-amber-400 mb-2">
                {language === 'ar' ? 'لا توجد شركات مسجّلة بعد. أنشئ ملف شركة أولاً.' : 'No companies yet. Create a company file first.'}
              </p>
              <Button onClick={() => navigate('/manage/cloudgm-agent/company/new')} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                {language === 'ar' ? 'إنشاء ملف شركة' : 'Create Company File'}
              </Button>
            </div>
          ) : (
            <Select
              value={form.company_id}
              onValueChange={(v) => setField('company_id', v)}
            >
              <SelectTrigger className="mt-2" data-testid="agent-residency-company-select">
                <SelectValue placeholder={language === 'ar' ? 'اختر شركة…' : 'Select a company…'} />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {language === 'ar' ? (c.company_name_ar || c.company_name_en) : (c.company_name_en || c.company_name_ar)}
                    {c.license_number ? ` — ${c.license_number}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </motion.section>
        )}

        {/* AI upload */}
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-cyan-400" />
            <h2 className="font-semibold">
              {language === 'ar' ? 'قراءة الهوية/الإقامة/التأشيرة تلقائياً (AI)' : 'Auto-read (AI)'}
            </h2>
          </div>
          <p className="text-sm text-[#808080] mb-4">
            {language === 'ar'
              ? 'ارفع صورة أو PDF (هوية إماراتية / إقامة / تأشيرة)، وسيتم استخراج البيانات تلقائياً.'
              : 'Upload an image or PDF (Emirates ID / residency / visa); AI will extract the fields.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <label className="flex items-center gap-2 px-4 py-2 bg-[#1a1a2e] border border-[#2a2a4e] rounded-xl cursor-pointer hover:bg-[#2a2a4e]">
              <Upload className="h-4 w-4" />
              <span className="text-sm">
                {file ? file.name : (language === 'ar' ? 'اختر الملف' : 'Choose file')}
              </span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFilePick} data-testid="agent-residency-file-input" />
            </label>
            <Button
              onClick={runAIExtract}
              disabled={!file || extracting}
              className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl"
              data-testid="agent-extract-residency-btn"
            >
              {extracting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Sparkles className="h-4 w-4 me-2" />}
              {language === 'ar' ? 'قراءة بالذكاء الاصطناعي' : 'Read with AI'}
            </Button>
          </div>
        </motion.section>

        {/* Fields */}
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div>
            <Label>{language === 'ar' ? 'نوع المستند' : 'Document type'}</Label>
            <Select value={form.doc_type} onValueChange={(v) => setField('doc_type', v)}>
              <SelectTrigger data-testid="agent-fld-doc-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="emirates_id">{language === 'ar' ? 'هوية إماراتية' : 'Emirates ID'}</SelectItem>
                <SelectItem value="residency">{language === 'ar' ? 'إقامة' : 'Residency'}</SelectItem>
                <SelectItem value="visa">{language === 'ar' ? 'تأشيرة' : 'Visa'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{language === 'ar' ? 'الجنس' : 'Gender'}</Label>
            <Input value={form.gender} onChange={(e) => setField('gender', e.target.value)} data-testid="agent-fld-gender" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'الاسم كاملاً (عربي)' : 'Full name (Arabic)'}</Label>
            <Input value={form.full_name_ar} onChange={(e) => setField('full_name_ar', e.target.value)} data-testid="agent-fld-name-ar" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'الاسم كاملاً (إنجليزي)' : 'Full name (English)'}</Label>
            <Input value={form.full_name_en} onChange={(e) => setField('full_name_en', e.target.value)} data-testid="agent-fld-name-en" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'الجنسية' : 'Nationality'}</Label>
            <Input value={form.nationality} onChange={(e) => setField('nationality', e.target.value)} data-testid="agent-fld-nationality" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'تاريخ الميلاد' : 'Date of birth'}</Label>
            <Input type="date" value={form.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} data-testid="agent-fld-dob" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'رقم الهوية الإماراتية' : 'Emirates ID number'}</Label>
            <Input value={form.emirates_id_number} onChange={(e) => setField('emirates_id_number', e.target.value)} data-testid="agent-fld-eid" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'رقم الجواز' : 'Passport number'}</Label>
            <Input value={form.passport_number} onChange={(e) => setField('passport_number', e.target.value)} data-testid="agent-fld-passport" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'رقم التأشيرة' : 'Visa number'}</Label>
            <Input value={form.visa_number} onChange={(e) => setField('visa_number', e.target.value)} data-testid="agent-fld-visa" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'رقم الملف' : 'File number'}</Label>
            <Input value={form.file_number} onChange={(e) => setField('file_number', e.target.value)} data-testid="agent-fld-fileno" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'UID' : 'UID number'}</Label>
            <Input value={form.uid_number} onChange={(e) => setField('uid_number', e.target.value)} data-testid="agent-fld-uid" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'نوع التأشيرة' : 'Visa type'}</Label>
            <Input value={form.visa_type} onChange={(e) => setField('visa_type', e.target.value)} data-testid="agent-fld-visa-type" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'الكفيل' : 'Sponsor'}</Label>
            <Input value={form.sponsor} onChange={(e) => setField('sponsor', e.target.value)} data-testid="agent-fld-sponsor" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'المهنة' : 'Profession'}</Label>
            <Input value={form.profession} onChange={(e) => setField('profession', e.target.value)} data-testid="agent-fld-profession" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'تاريخ الإصدار' : 'Issue date'}</Label>
            <Input type="date" value={form.issue_date} onChange={(e) => setField('issue_date', e.target.value)} data-testid="agent-fld-issue" />
          </div>
          <div>
            <Label>{language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry date'}</Label>
            <Input type="date" value={form.expiry_date} onChange={(e) => setField('expiry_date', e.target.value)} data-testid="agent-fld-expiry" />
          </div>
          <div className="md:col-span-2">
            <Label>{language === 'ar' ? 'مكان الإصدار' : 'Place of issue'}</Label>
            <Input value={form.place_of_issue} onChange={(e) => setField('place_of_issue', e.target.value)} data-testid="agent-fld-place" />
          </div>
          <div className="md:col-span-2">
            <Label>{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="agent-fld-notes" />
          </div>
        </motion.section>

      <div className="flex justify-end gap-3">
        <Button variant="outline" className="border-[#2a2a4e] rounded-xl text-white" onClick={() => navigate('/manage/cloudgm-agent')}>
          {language === 'ar' ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !form.company_id}
          className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl"
          data-testid="agent-residency-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
          {language === 'ar' ? 'حفظ والعودة للقائمة الرئيسية' : 'Save & back to hub'}
        </Button>
      </div>
    </AgentPageShell>
  );
};

export default AgentResidencyCreatePage;
